// Shared "report what's on screen to SAM" mechanism — replaces the old per-surface pattern of
// threading an `onXContext` callback prop through every intermediate component between a leaf
// card and ChatPage. That pattern broke twice in one session: Quick Quote's report was never
// wired to the right backend key at all, and ProposalDisplayCard's DRAFT-status render branch
// silently lacked the report effect its sibling branch had. Both bugs were "one link in a long
// prop chain was missing or wrong" — a class of bug this hook makes structurally impossible: any
// component, however deeply nested under <SamSurfaceProvider>, calls useSamSurfaceContext directly
// and there is no chain to break.
//
// `key` becomes the `active_<key>` field the backend (api-service/app/services/llm/ui_context.py)
// parses via its `_norm_*` functions — see that file for the exact shape each key expects
// (active_task, active_meeting, active_note, active_draft, active_quick_quote,
// active_proposal_surface, active_engagement_workflow, active_workflow_settings). Passing `null`
// explicitly reports the surface as closed; unmounting the component does this automatically, so
// a new surface never needs its own cleanup effect to avoid leaving stale context behind.
import { createContext, useContext, useEffect, useId, useRef } from 'react';

// ownerId is a stable per-instance ID so emitSurfaceContext can reject null clears from cards
// that never owned the current active context (e.g. 29 collapsed TaskCards that never wrote
// active_task clearing the one that did).
// Returns false when the emit was silently dropped (e.g. conversationId not yet set) so the
// hook knows NOT to advance lastSent — it will retry automatically when the context re-renders.
export type SamSurfaceEmit = (key: string, state: unknown, ownerId: string) => boolean;

export const SamSurfaceContext = createContext<SamSurfaceEmit | null>(null);

/** A section of the engagement card (property, assessment, transition_capture, billing, site_board, job_card) that is
 *  on screen / in use. Reported as `active_<section>_section` so SAM's scope follows it (api-service cards/scope.py:
 *  its tools and rule block are offered only while it is the section in use). Pass `active=false` to report it closed. */
export function useSamSectionContext(
  section: string,
  ids: { engagement_id?: string | null; property_id?: string | null },
  active = true,
): void {
  const has = !!(ids.engagement_id || ids.property_id);
  useSamSurfaceContext(
    `${section}_section`,
    active && has ? { ...(ids.engagement_id ? { engagement_id: ids.engagement_id } : {}), ...(ids.property_id ? { property_id: ids.property_id } : {}) } : null,
  );
}

export function useSamSurfaceContext<T>(key: string, state: T | null): void {
  const emit = useContext(SamSurfaceContext);
  // Initialize to 'null' (JSON.stringify(null)) so collapsed cards — whose state starts as null —
  // see serialized === lastSent and skip the first emit entirely, avoiding the N-card storm on mount.
  const lastSent = useRef<string>('null');
  // Stable per-instance ID (React 18 useId) passed with every emit so the emit function can enforce
  // ownership: only the card that last wrote a key is allowed to clear it with null.
  const ownerId = useId();

  // Deliberately no dependency array: `state` is typically a fresh object literal every render, so
  // an identity-based dep would re-fire on every render regardless of whether any value actually
  // changed. Runs after every commit and self-gates on a value comparison instead.
  useEffect(() => {
    const serialized = JSON.stringify(state);
    if (serialized === lastSent.current) return;
    // Only advance lastSent when the emit actually reached the socket. If emitSurfaceContext
    // returns false (conversationId not yet set), leave lastSent as-is so the next render —
    // triggered by emitSurfaceContext gaining a new identity once conversationId arrives —
    // retries automatically.
    const sent = emit?.(key, state, ownerId);
    if (sent !== false) lastSent.current = serialized;
  });

  // Auto-clear on TRUE unmount only (surface closed) — the single most common gap in the old
  // pattern, since it required every leaf to remember its own cleanup effect (Quick Quote's fix
  // added one by hand; this makes that unnecessary for every future surface). `emit` itself
  // changes identity whenever the conversation's mode flips (text ↔ interactive), since
  // emitSurfaceContext is a useCallback keyed on mode — depending on `emit` directly here would
  // re-run this effect's cleanup on every mode switch too, wrongly clearing every mounted
  // surface's context each time voice starts (found live: active_task went null the moment
  // interactive mode connected, despite the task detail never closing). The ref keeps the cleanup
  // reading the LATEST emit without making mode changes look like an unmount.
  const emitRef = useRef(emit);
  emitRef.current = emit;
  // A plain `return () => emit(key, null)` here also fires on React 18 StrictMode's dev-only
  // double-invoke (setup → cleanup → setup, synchronously, against the SAME instance, to surface
  // missing cleanups) — indistinguishable from a real unmount to a single effect. Found live: right
  // after WorkflowSettingsCard mounted, Redis's active_workflow_settings was wiped back to null
  // moments later even though the card never closed. Deferring the clear to a microtask and
  // cancelling it if setup runs again first tells the two apart: StrictMode's remount re-runs setup
  // synchronously, in the same turn, before the microtask can flush; a real unmount never does.
  const pendingClear = useRef(false);
  useEffect(() => {
    pendingClear.current = false;
    return () => {
      pendingClear.current = true;
      queueMicrotask(() => {
        if (pendingClear.current) emitRef.current?.(key, null, ownerId);
      });
    };
  }, [key, ownerId]);
}
